import React, { Fragment } from 'react'
import {
  array, object, bool, string,
} from 'prop-types'
import axios from 'axios'

import QueryToJson from 'Utils/QueryToJson'
import JsonToQuery from 'Utils/JsonToQuery'

import ItemsListingItem from './Components/ItemsListingItem'
import CustomModelRequestCard from './Components/CustomModelRequestCard'
import BrowseAllModelsCard from './Components/BrowseAllModelsCard'
import WildcatBannerCard from './Components/WildcatBannerCard'
import ItemsListingGallery from './Components/ItemsListingGallery'
import { ItemTracker } from './Components/ItemTracker'
import { TrackingConsumer } from './Tracking.context'

const dropFirstProductsLimit = 200

class ItemsListing extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      cart: props.cart,
      data: props.data,
      fetchParams: props.fetchParams || {},
    }

    this.autoLoadCount = 0
    this.isFetching = false

    this.handleCartUpdate = this.handleCartUpdate.bind(this)
    this.onLoadMore = this.onLoadMore.bind(this)
    this.onLoadMoreSuccess = this.onLoadMoreSuccess.bind(this)
  }

  componentDidMount() {
    App.events.subscribe('app:cart:updated', this.handleCartUpdate)
  }

  componentDidUpdate(prevProps) {
    if (this.props.data !== prevProps.data) this.setState({ data: this.props.data })
  }

  componentWillUnmount() {
    App.events.remove('app:cart:updated', this.handleCartUpdate)
  }

  onLoadMore(event) {
    this.isFetching = true

    event && event.preventDefault()

    const params = this.state.fetchParams
    params.page = params.page ? params.page + 1 : 2

    this.setState({ fetchParams: params }, () => {
      axios.get('/api/internal/items', { params })
        .then(this.onLoadMoreSuccess)
        .catch((error) => error)
    })
  }

  onLoadMoreSuccess(response) {
    if (response.data.data.length > 0) {
      const params = QueryToJson(window.location.search.substr(1))
      params.page = params.page ? parseInt(params.page) + 1 : 2

      history.pushState({}, document.title, `${window.location.pathname}?${JsonToQuery(params)}`)

      let newData = this.state.data.concat(response.data.data)

      if (newData.length > dropFirstProductsLimit) {
        // Due to performance reasons, start cutting first products

        newData = newData.slice(response.data.data.length)
      }

      this.setState({
        data: newData,
        cart: response.data.meta.cart,
      }, () => this.isFetching = false)
    } else {
      this.setState({ lastIsPageReached: true }, () => this.isFetching = false)
    }
  }

  handleCartUpdate(legacyCartResponseObject) {
    this.setState({
      cart: {
        value: legacyCartResponseObject.meta.finalPrice,
        creditsAvailable: legacyCartResponseObject.meta.storeCreditsLeft,
        applicableCldValue: legacyCartResponseObject.meta.minPurchaseAmountForCld,
        saleOffDiscount: legacyCartResponseObject.meta.saleOffDiscount,
        isSaleOff: legacyCartResponseObject.meta.saleCampaignActive,
        itemIds: legacyCartResponseObject.data.map((obj) => obj.attributes.itemId),
      },
    })
  }

  render() {
    const doesntHaveItems = this.state.data.length === 0

    return (
      <div className={this.props.isWithCarousel ? '' : `${this.props.isAutoAdjusting ? 'row row--self-adjusting' : 'row'}`}>
        <TrackingConsumer>
          {({ onImpression, onClick, onAddToCart }) => (
            <ItemsListingGallery skipGallery={!this.props.isWithCarousel} itemsCount={this.state.data.length}>
              {this.state.data.map((item, index) => (
                <Fragment key={item.id}>
                  {this.props.isFreeListing && index === 4 ? (
                    <WildcatBannerCard />
                  ) : (
                    <ItemTracker
                      handleImpression={() => onImpression(item, index, this.props.perRowType, this.state.cart)}
                      isTrackingEnabled={this.props.isTrackingEnabled}
                      classNames={this.props.isWithCarousel ? 'carousel-slide' : this.props.classNames}
                    >
                      <ItemsListingItem
                        handleAddToCart={() => onAddToCart(item, index, this.props.gaList, this.state.cart)}
                        isTrackingEnabled={this.props.isTrackingEnabled}
                        key={item.id}
                        cart={this.state.cart || undefined}
                        // TODO: Remove this line, once react-lazy-load-image-component is updated and working
                        imageVisibleByDefault
                        viewerIsAdult={this.props.viewerIsAdult}
                        userSignedIn={this.props.userSignedIn}
                        onClick={() => onClick(item, index, this.props.gaList, this.state.cart)}
                        withPremiumRibbon={this.props.withPremiumRibbons}
                        withCheckbox={this.props.withCheckboxes}
                        isCompact={this.props.isCompact}
                        isWithCarousel={this.props.isWithCarousel}
                        displayCarouselButton={!this.props.isWithCarousel}
                        isGalleryEnabled={this.props.isGalleryEnabled}
                        {...item}
                      />
                    </ItemTracker>
                  )}
                </Fragment>
              ))}
            </ItemsListingGallery>
          )}
        </TrackingConsumer>
        {this.props.withCustomModelRequestCard && (
          <CustomModelRequestCard locale={this.props.locale} isAlone={doesntHaveItems} />
        )}
        {this.props.withBrowseAllModelsCard && (
          <BrowseAllModelsCard
            isSaleOff={this.state.cart.isSaleOff}
            keywords={this.props.keywords || ''}
          />
        )}
      </div>
    )
  }
}

ItemsListing.propTypes = {
  data: array.isRequired,
  cart: object,
  viewerIsAdult: bool,
  userSignedIn: bool,
  gridType: string,
  withCustomModelRequestCard: bool,
  withBrowseAllDiscountedCard: bool,
  withPremiumRibbons: bool,
  withCheckboxes: bool,
  isCompact: bool,
  isFreeListing: bool,
  gaList: string,
  keywords: string,
  classNames: string,
  isWithCarousel: bool,
}

ItemsListing.defaultProps = {
  cart: null,
  gridType: '',
  viewerIsAdult: false,
  userSignedIn: false,
  withCustomModelRequestCard: false,
  withBrowseAllModelsCard: false,
  withPremiumRibbons: false,
  withCheckboxes: false,
  isCompact: false,
  isFreeListing: false,
  gaList: '',
  keywords: '',
  classNames: '',
  isWithCarousel: false,
}

export default ItemsListing
